import { useState } from 'react'
import { Icon, useMobile } from './shared'
import { useT } from './i18n'
import { isNativeApp, printSlip, readPrinter, savePrinter, type PrinterPrefs } from './thermal'

/* The ticket printer for this device: how to reach it, the roll width, and
   whether a booking prints its slip by itself. Kept on the device, because
   each desk has its own printer. */

export default function PrinterSettings() {
  const { t } = useT()
  const { settings, toast } = useMobile()
  const [p, setP] = useState<PrinterPrefs>(() => readPrinter())
  const update = (patch: Partial<PrinterPrefs>) => {
    const next = { ...p, ...patch }
    setP(next); savePrinter(next)
  }

  async function test() {
    try {
      await printSlip({
        clinicName: settings?.clinicName ?? 'Clinic',
        clinicPhone: settings?.phone,
        heading: 'TEST PRINT',
        serial: 7,
        title: 'Printer is working',
        lines: [['Paper', `${p.paper} mm`], ['Method', p.method === 'rawbt' ? 'RawBT' : 'Print dialog']],
        footer: 'Clinic Desk',
      }, p)
      if (p.method === 'rawbt') toast({ kind: 'info', text: t('pr.sentRawbt') })
    } catch {
      toast({ kind: 'error', text: t('pr.failed') })
    }
  }

  return (
    <div className="m-section">
      <div className="m-section-head"><h2>{t('pr.title')}</h2></div>
      <p className="m-note">{t('pr.note')}</p>
      <div className="m-card m-form">
        <div className="m-field wide">
          <label id="pr-how">{t('pr.method')}</label>
          <div className="m-seg tight" role="radiogroup" aria-labelledby="pr-how">
            <button type="button" role="radio" aria-checked={p.method === 'system'} className={p.method === 'system' ? 'is-on' : ''} onClick={() => update({ method: 'system' })}>{t('pr.system')}</button>
            <button type="button" role="radio" aria-checked={p.method === 'rawbt'} className={p.method === 'rawbt' ? 'is-on' : ''} onClick={() => update({ method: 'rawbt' })}>{t('pr.rawbt')}</button>
          </div>
          <span className="hint">{p.method === 'rawbt' ? t('pr.rawbtHint') : isNativeApp() ? t('pr.systemInAppHint') : t('pr.systemHint')}</span>
        </div>
        <div className="m-field wide">
          <label id="pr-paper">{t('pr.paper')}</label>
          <div className="m-seg tight" role="radiogroup" aria-labelledby="pr-paper">
            {([58, 80] as const).map((w) => (
              <button key={w} type="button" role="radio" aria-checked={p.paper === w} className={p.paper === w ? 'is-on' : ''} onClick={() => update({ paper: w })}>{w} mm</button>
            ))}
          </div>
        </div>
      </div>
      <div className="m-card m-menu">
        <label className="m-menu-item" htmlFor="pr-auto">
          <span className="ico"><Icon name="printer" size={18} /></span>
          <span className="lbl">
            {t('pr.auto')}
            <span className="m-note" style={{ display: 'block', fontWeight: 500 }}>{p.autoPrint ? t('pr.autoOn') : t('pr.autoOff')}</span>
          </span>
          <input id="pr-auto" className="m-switch" type="checkbox" checked={p.autoPrint} onChange={(e) => update({ autoPrint: e.target.checked })} />
        </label>
        <button type="button" className="m-menu-item" onClick={() => void test()}>
          <span className="ico"><Icon name="check" size={18} /></span>
          <span className="lbl">{t('pr.test')}</span>
          <Icon name="chevron" size={16} />
        </button>
      </div>
    </div>
  )
}
